<template>
  <section class="technical-specs-section">
    <!-- Section Header -->
    <div class="specs-header">
      <h3 class="specs-title">
        <i>Technical</i> Specifications
      </h3>
      <p class="specs-description">
        Built on proven blockchain infrastructure with industry-standard cryptography. All operations are transparent and verifiable on-chain.
      </p>
    </div>

    <!-- Specs List -->
    <div class="specs-list">
      <!-- Section Title -->
      <div class="specs-list-header">
        <h4 class="specs-subtitle">
          Under the hood
        </h4>
      </div>

      <!-- Blockchain -->
      <div class="spec-card">
        <h5 class="spec-card-title">
          📋 Blockchain
        </h5>
        <div class="spec-card-content">
          <div class="spec-item">
            <span class="spec-value">Base Mainnet</span>
          </div>
          <div class="spec-item">
            <span class="spec-label">USDC Contract:</span>
            <div class="spec-value-group">
              <code class="spec-code">0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913</code>
              <button
                class="copy-button"
                @click="copyToClipboard('0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', 'USDC address')"
                :class="{ copied: copiedItem === 'USDC address' }"
              >
                {{ copiedItem === 'USDC address' ? '✓' : '📋' }}
              </button>
            </div>
          </div>
          <div class="spec-item">
            <span class="spec-value">ERC-20 token standard with EIP-2612 Permit extension</span>
          </div>
          <div class="spec-item">
            <a
              href="https://basescan.org/token/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"
              target="_blank"
              rel="noopener noreferrer"
              class="spec-verify-link"
            >
              Verify on BaseScan ↗
            </a>
          </div>
        </div>
      </div>

      <!-- Cryptographic Standards -->
      <div class="spec-card">
        <h5 class="spec-card-title">
          🔐 Cryptographic Standards
        </h5>
        <div class="spec-card-content">
          <div class="spec-item">
            <span class="spec-label">Wallet Generation:</span>
            <span class="spec-value">BIP39 (12-word mnemonic) + BIP44 derivation path</span>
          </div>
          <div class="spec-item">
            <span class="spec-label">Encryption:</span>
            <span class="spec-value">AES-256-GCM with PBKDF2 (500k iterations)</span>
          </div>
          <div class="spec-item">
            <span class="spec-label">Signatures:</span>
            <span class="spec-value">EIP-712 (withdrawal intents) + EIP-2612 (token permits)</span>
          </div>
        </div>
      </div>

      <!-- Non-Custodial Architecture -->
      <div class="spec-card">
        <h5 class="spec-card-title">
          🛡️ Non-Custodial Architecture
        </h5>
        <div class="spec-card-content">
          <div class="spec-item">
            <span class="spec-label">Wallet Generation:</span>
            <span class="spec-value">Client-side in your browser</span>
          </div>
          <div class="spec-item">
            <span class="spec-label">Private Keys:</span>
            <span class="spec-value">Never transmitted to servers</span>
          </div>
          <div class="spec-item">
            <span class="spec-label">Gasless Withdrawals:</span>
            <span class="spec-value">Signed transactions with nonce & deadline protection</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const copiedItem = ref<string | null>(null)

async function copyToClipboard(text: string, itemName: string) {
  try {
    await navigator.clipboard.writeText(text)
    copiedItem.value = itemName
    setTimeout(() => {
      copiedItem.value = null
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>

<style scoped>
.technical-specs-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 16px;
  gap: 24px;
  background: #4B71EE;
  border-radius: 24px;
  max-width: 100%;
  overflow: hidden;
}

.specs-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  align-self: stretch;
}

.specs-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: 0.01em;
  color: #FFFFFF;
  align-self: stretch;
}

.specs-description {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: #FFFFFF;
  align-self: stretch;
}

.specs-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  align-self: stretch;
}

.specs-list-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 0px;
  gap: 10px;
  align-self: stretch;
}

.specs-subtitle {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0.01em;
  color: #FFFFFF;
  flex-grow: 1;
}

.spec-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 12px;
  gap: 12px;
  background: #FFFFFF;
  border-radius: 12px;
  align-self: stretch;
}

.spec-card-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  letter-spacing: 0.01em;
  color: #182855;
  align-self: stretch;
}

.spec-card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
}

.spec-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}

.spec-label {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: #3A55A6;
  min-width: 100px;
}

.spec-value {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: #182855;
  flex: 1;
}

.spec-link {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
}

.spec-link-external {
  color: #4B71EE;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s;
}

.spec-link-external:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.spec-value-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}

.spec-code {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  background: #F5F7FA;
  padding: 4px 8px;
  border-radius: 4px;
  color: #182855;
  word-break: break-all;
  overflow-wrap: break-word;
  max-width: 100%;
  flex: 1;
}

.copy-button {
  background: #EBEFFD;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  min-width: 32px;
}

.copy-button:hover {
  background: #D4DFFB;
}

.copy-button.copied {
  background: #4B71EE;
}

.spec-verify-link {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: #4B71EE;
  text-decoration: none;
  transition: opacity 0.2s;
}

.spec-verify-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}
</style>
